import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Phone, ShieldCheck, Clock, Scale, Star, MessageSquare,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SeoHead, { firmJsonLd } from "@/components/SeoHead";
import SpeedPromiseBadge from "@/components/SpeedPromiseBadge";
import SocialProofTicker from "@/components/SocialProofTicker";
import AnimatedCounter from "@/components/AnimatedCounter";
import { useT } from "@/i18n/I18nContext";

const HERO_IMG =
  "https://images.pexels.com/photos/8569166/pexels-photo-8569166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CITY_DATA = {
  brooklyn: {
    name: "Brooklyn",
    state: "NY",
    landmarks: "BQE, Belt Parkway, Atlantic Avenue, Ocean Parkway",
  },
  queens: {
    name: "Queens",
    state: "NY",
    landmarks: "LIE, Grand Central Parkway, Van Wyck, Cross Bay Blvd",
  },
  bronx: {
    name: "Bronx",
    state: "NY",
    landmarks: "Major Deegan, Cross Bronx Expy, Bruckner",
  },
  manhattan: {
    name: "Manhattan",
    state: "NY",
    landmarks: "FDR Drive, West Side Highway, Harlem River Drive",
  },
  newark: {
    name: "Newark",
    state: "NJ",
    landmarks: "Route 21, I-78, I-280, Garden State Parkway",
  },
  "jersey-city": {
    name: "Jersey City",
    state: "NJ",
    landmarks: "Holland Tunnel, Routes 1&9, Hudson County Blvd",
  },
};

export default function CityLanding({ citySlug }) {
  const t = useT();
  const data = CITY_DATA[citySlug];
  if (!data) return <Navigate to="/" replace />;

  const headline = t("city.headline", { city: data.name });
  const description = `${headline} ${t("city.subhead")}`;

  return (
    <div className="min-h-screen" data-testid={`city-page-${citySlug}`}>
      <SeoHead
        title={`${data.name} Car Accident Lawyer · NY/NJ MVA Attorneys | Apex MVA`}
        description={`${headline} Free case review for ${data.name} accident victims. No fee unless we win.`}
        keywords={`${data.name} car accident lawyer, ${data.name} personal injury attorney, MVA ${data.name}, motor vehicle accident ${data.state}`}
        jsonLd={firmJsonLd()}
      />

      <div className="w-full bg-[#11322A] text-white">
        <div className="mx-auto max-w-7xl px-6 py-2 text-center text-xs font-semibold tracking-wide lg:px-12">
          {t("urgency.banner")}
        </div>
      </div>

      <Navbar />

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/92 to-[#FDFBF7]/55"
          aria-hidden
        />
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-12 lg:gap-16 lg:px-12 lg:py-24">
          <div className="lg:col-span-7">
            <span className="pill" data-testid="city-pill">
              <span className="h-2 w-2 rounded-full bg-[#E85D04]" />
              {t("city.local_pill", { city: data.name })}
            </span>
            <h1
              className="mt-6 font-display text-4xl font-black tracking-tighter text-[#11322A] sm:text-5xl lg:text-6xl"
              data-testid="city-h1"
            >
              {headline}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#5C6B68] sm:text-lg">
              {t("city.subhead")}
            </p>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#0B1317]">
              {t("city.body", { city: data.name, landmarks: data.landmarks })}
            </p>
            <SpeedPromiseBadge />
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/intake" className="btn-primary" data-testid="city-cta-intake">
                {t("city.cta_intake")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/chat" className="btn-secondary" data-testid="city-cta-chat">
                <MessageSquare className="h-4 w-4" /> {t("city.cta_chat")}
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#5C6B68]">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /> {t("city.trust1")}
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /> {t("city.trust2")}
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /> {t("city.trust3")}
              </span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="card-soft p-8">
              <div className="label-cap">{t("hero.stats.title")}</div>
              <div className="mt-6 grid grid-cols-2 gap-6">
                {[
                  { n: "$340M+", l: t("hero.stat.recovered") },
                  { n: "12,400+", l: t("hero.stat.cases") },
                  { n: "4.9★", l: t("hero.stat.rating") },
                  { n: "24/7", l: t("hero.stat.hotline") },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-3xl font-black text-[#11322A]">
                      <AnimatedCounter value={s.n} />
                    </div>
                    <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#5C6B68]">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
              <div
                className="mt-8 rounded-xl p-5"
                style={{ background: "var(--bg)", border: "1px solid var(--border)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-[#11322A] text-white">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#5C6B68]">
                      {t("hero.help_now")}
                    </div>
                    <a
                      href="tel:+18005551234"
                      className="font-display text-xl font-black text-[#11322A] hover:text-[#E85D04]"
                      data-testid="city-phone"
                    >
                      1-800-555-1234
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 sm:grid-cols-3 lg:px-12">
          {[
            { i: ShieldCheck, t: t("why.feature.vetted") },
            { i: Clock, t: t("why.feature.response") },
            { i: Scale, t: t("why.feature.contingency") },
          ].map((f) => (
            <div key={f.t} className="card-soft p-6">
              <f.i className="h-5 w-5 text-[#E85D04]" />
              <div className="mt-3 font-display text-base font-bold text-[#0B1317]">{f.t}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#11322A] py-16 text-white">
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-12">
          <div className="flex justify-center gap-1 text-[#E85D04]">
            {["a", "b", "c", "d", "e"].map((id) => (
              <Star key={`star-${id}`} className="h-5 w-5 fill-current" />
            ))}
          </div>
          <blockquote className="mt-6 font-display text-xl font-bold leading-snug sm:text-2xl">
            "{t("testimonial.quote")}"
          </blockquote>
          <div className="mt-6 text-sm text-white/70">{t("testimonial.attribution")}</div>
          <Link to="/intake" className="btn-primary mt-8 inline-flex" data-testid="city-cta-bottom">
            {t("city.cta_intake")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
      <SocialProofTicker />

      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E0E4E2] bg-white/95 px-3 py-3 backdrop-blur md:hidden"
        data-testid="city-mobile-sticky-cta"
      >
        <div className="mx-auto flex max-w-md items-center gap-2">
          <a
            href="tel:+18005551234"
            className="btn-secondary !py-2.5 flex-1 text-xs"
            data-testid="city-mobile-call"
          >
            <Phone className="h-3.5 w-3.5" /> {t("mobile.call")}
          </a>
          <a
            href="sms:+18005551234"
            className="btn-secondary !py-2.5 flex-1 text-xs"
            data-testid="city-mobile-text"
          >
            <MessageSquare className="h-3.5 w-3.5" /> {t("mobile.text")}
          </a>
          <Link to="/intake" className="btn-primary !py-2.5 flex-[2] text-xs" data-testid="city-mobile-intake">
            {t("free_review")}
          </Link>
        </div>
      </div>
    </div>
  );
}
